/**
 * reviewAgentClient — thin HTTP client for the review_agent sidecar.
 *
 * SRP: this module ONLY talks to the sidecar (http://localhost:8001
 * by default). It does NOT render UI, hold React state, or decide
 * when to fetch — those are the hook's concerns (useReviewAgent).
 *
 * Adapter pattern (per vault principles): if the sidecar surface
 * moves, there's one file to update. All callers depend on this
 * module's exported functions, not on `fetch` directly.
 *
 * Configuration:
 *   VITE_REVIEW_AGENT_BASE (env var) → defaults to ``/review-agent``
 *   (a same-origin proxy path). The Vite dev server is configured
 *   in addin/vite.config.ts to forward ``/review-agent/*`` to
 *   ``http://localhost:8001/*`` (path prefix stripped).
 *
 *   Why a proxy instead of pointing fetch directly at
 *   ``http://localhost:8001``: the add-in is served over HTTPS
 *   (self-signed cert in dev). A direct cross-origin fetch from
 *   HTTPS to an HTTP origin is blocked by the browser / WebView's
 *   mixed-content policy BEFORE CORS is evaluated. Going through
 *   the same-origin proxy sidesteps both mixed-content AND CORS
 *   in one shot.
 *
 *   For production deployments where the sidecar is reachable on
 *   HTTPS (or hosted at a different origin entirely), set
 *   VITE_REVIEW_AGENT_BASE to that origin and skip the proxy.
 *
 * Spec: docs/specs/10-review-agent-sidecar.md § HTTP surface.
 */

import type { AuditIndexResponse, AuditRecord, HealthResponse, ReviewResponse } from "../components/review-agent/types";

// Same-origin proxy path. Resolved by addin/vite.config.ts to
// http://localhost:8001 in dev. Fetch from the HTTPS-served add-in
// stays same-origin → no mixed-content blocking, no CORS.
const DEFAULT_BASE = "/review-agent";

function baseUrl(): string {
  const envBase = (import.meta as unknown as { env?: { VITE_REVIEW_AGENT_BASE?: string } }).env?.VITE_REVIEW_AGENT_BASE;
  return envBase || DEFAULT_BASE;
}

/**
 * Thin fetch wrapper that throws typed errors. Callers catch
 * ReviewAgentFetchError in the hook and surface a retry/error UI.
 */
export class ReviewAgentFetchError extends Error {
  constructor(
    public status: number,
    public path: string,
    public detail: string,
  ) {
    super(`review_agent ${path} → ${status}: ${detail}`);
    this.name = "ReviewAgentFetchError";
  }
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const url = `${baseUrl()}${path}`;
  let resp: Response;
  try {
    resp = await fetch(url, { signal });
  } catch (err) {
    // Network error — sidecar not running, CORS blocked, etc.
    throw new ReviewAgentFetchError(0, path, err instanceof Error ? err.message : "network error");
  }
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const body = (await resp.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch {
      // Body wasn't JSON — keep statusText
    }
    throw new ReviewAgentFetchError(resp.status, path, detail);
  }
  return (await resp.json()) as T;
}

// ---------------------------------------------------------------------------
// Public API — one function per endpoint
// ---------------------------------------------------------------------------

export function fetchHealth(signal?: AbortSignal): Promise<HealthResponse> {
  return getJson<HealthResponse>("/health", signal);
}

export function fetchAuditIndex(session: string, signal?: AbortSignal): Promise<AuditIndexResponse> {
  return getJson<AuditIndexResponse>(`/audit/${encodeURIComponent(session)}`, signal);
}

export function fetchAudit(session: string, target: string, signal?: AbortSignal): Promise<AuditRecord> {
  const s = encodeURIComponent(session);
  const t = encodeURIComponent(target);
  return getJson<AuditRecord>(`/audit/${s}/${t}`, signal);
}

export function fetchReview(session: string, target: string, signal?: AbortSignal): Promise<ReviewResponse> {
  const s = encodeURIComponent(session);
  const t = encodeURIComponent(target);
  return getJson<ReviewResponse>(`/review/${s}/${t}`, signal);
}

/**
 * URL for the live per-persona review progress SSE stream. Consumed by
 * ``useReviewProgress`` via ``EventSource`` so the Review panel can render the
 * real key steps while a report is produced (instead of a single static line).
 * Uses a dedicated ``/progress`` prefix so the ``{target:path}`` catch-all on
 * ``GET /review`` doesn't swallow it (matches review_agent/server.py).
 */
export function reviewProgressEventsUrl(session: string, target: string): string {
  const s = encodeURIComponent(session);
  const t = encodeURIComponent(target);
  return `${baseUrl()}/progress/${s}/${t}`;
}
